/**
 * NEWS-06 — notícias lidas e salvas, por usuário, só neste aparelho (Q15). Cada usuário tem a
 * própria chave no localStorage (aparelho compartilhado não mistura estado); marcas com mais de
 * 90 dias são podadas ao carregar. Sem storage (modo privado, cota), o estado vive só na memória.
 */
import { create } from 'zustand'

export const NEWS_STATE_RETENTION_DAYS = 90
const KEY_PREFIX = 'synap-news-state:'
const DAY_MS = 86_400_000

type Marks = Record<string, string>
type Persisted = { read: Marks; saved: Marks }

const storageKey = (userId: string) => `${KEY_PREFIX}${userId}`

const prune = (marks: unknown, now: number): Marks => {
  if (!marks || typeof marks !== 'object') return {}
  const out: Marks = {}
  for (const [id, at] of Object.entries(marks as Record<string, unknown>)) {
    const time = typeof at === 'string' ? Date.parse(at) : NaN
    if (Number.isFinite(time) && now - time <= NEWS_STATE_RETENTION_DAYS * DAY_MS) out[id] = at as string
  }
  return out
}

const load = (userId: string, now: number): Persisted => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return { read: {}, saved: {} }
    const parsed = JSON.parse(raw) as Partial<Persisted>
    return { read: prune(parsed.read, now), saved: prune(parsed.saved, now) }
  } catch {
    return { read: {}, saved: {} }
  }
}

const save = (userId: string | null, state: Persisted) => {
  if (!userId) return
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state))
  } catch {
    /* storage cheio ou bloqueado: segue só na memória */
  }
}

interface NewsItemStateStore extends Persisted {
  userId: string | null
  /** Carrega (e poda) o estado do usuário; `null` limpa da memória. */
  setUser: (userId: string | null) => void
  markRead: (id: string) => void
  markAllRead: (ids: ReadonlyArray<string>) => void
  toggleSaved: (id: string) => void
}

export const useNewsItemStateStore = create<NewsItemStateStore>((set, get) => {
  const update = (patch: (state: Persisted, at: string) => Persisted) => {
    const current = get()
    const next = patch({ read: current.read, saved: current.saved }, new Date().toISOString())
    set(next)
    save(current.userId, next)
  }
  return {
    userId: null,
    read: {},
    saved: {},
    setUser: (userId) => {
      if (get().userId === userId) return
      set({ userId, ...(userId ? load(userId, Date.now()) : { read: {}, saved: {} }) })
    },
    markRead: (id) => {
      if (get().read[id]) return
      update((state, at) => ({ ...state, read: { ...state.read, [id]: at } }))
    },
    markAllRead: (ids) =>
      update((state, at) => ({ ...state, read: { ...state.read, ...Object.fromEntries(ids.filter((id) => !state.read[id]).map((id) => [id, at])) } })),
    toggleSaved: (id) =>
      update((state, at) => {
        const saved = { ...state.saved }
        if (saved[id]) delete saved[id]
        else saved[id] = at
        return { ...state, saved }
      }),
  }
})

/** «N não lidas» do Mais e da barra lateral. */
export const unreadCount = (ids: ReadonlyArray<string>, read: Marks): number => ids.filter((id) => !read[id]).length
